import FixForm from "@/elements-react/fix-form";
import { Input } from "@/uikit";
import { Icon } from "@/elements-react/fix-icon";
import phoneIcon from "@/assets/phone.svg";
import mailIcon from "@/assets/gone-page/mailIcon.svg";
import { useCallback, useState } from "preact/hooks";
import { t } from "i18next";
import React from "preact/compat";
import styles from "./lead-form.module.scss";
import lockIcon from "@/assets/lock.svg";
import { FixButton } from "@/elements-react/fix-button";
import { Textarea } from "@/uikit/Textarea/Textarea.tsx";
import { useServices } from "@/services-context";
import { LeadCreateDocument } from "@/generated/graphql/b2c";
import { EMAIL_REGEX, PHONE_REGEX } from "@/util/constants";
import { LeadFormSuccess } from "@/pages-react/product-page/lead-form/success.tsx";

export interface LeadFormProps {
  title: string;
  subTitle: string;
  buttonText: string;
  formName: string;
  mpn?: string;
  customize?: {
    buttonIcon: null | string;
  };
  formAttributes: object;
}

type FormErrors = {
  email: string;
  phone: string;
  comment: string;
};

const sanitizePhone = (value: string) => {
  const processed = value.replace(/[^\d\s\-()+]/g, "");

  if (processed.startsWith("+")) {
    return `+${processed.slice(1).replace(/\+/g, "")}`;
  }

  return processed.replace(/\+/g, "");
};

export const LeadForm: React.FC<LeadFormProps> = ({ title, subTitle, buttonText, formName, mpn, customize, formAttributes }) => {
  const services = useServices();
  const [formData, setFormData] = useState({ email: "", phone: "", comment: "" });
  const [formErrors, setFormErrors] = useState<FormErrors>({
    email: "",
    phone: "",
    comment: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const changeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target as HTMLInputElement;

    let processedValue = value;

    if (name === "phone") {
      processedValue = sanitizePhone(value);
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));

    setFormErrors((prev) => {
      if (prev[name as keyof FormErrors]) {
        return { ...prev, [name]: "" };
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const newErrors: FormErrors = {
      email: "",
      phone: "",
      comment: "",
    };

    const trimmedPhone = formData.phone.trim();
    const hasDigits = /\d/.test(trimmedPhone);

    if (!trimmedPhone) {
      newErrors.phone = t("cart.form.pleaseFillField", "Please fill out this field");
    } else if (!hasDigits) {
      newErrors.phone = t("cart.form.pleaseFillField", "Please fill out this field");
      setFormData((prev) => ({ ...prev, phone: "" }));
    } else if (!PHONE_REGEX.test(trimmedPhone)) {
      newErrors.phone = t("znvy8ierfberif48.phone-number-short", "Phone number is too short");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("cart.form.pleaseFillField", "Please fill out this field");
    } else if (!EMAIL_REGEX.test(formData.email.trim())) {
      newErrors.email = t("q0qp94g9qwfquw9e.invalid-email", "Please enter a valid email address");
    }

    if (!formData.comment.trim()) {
      newErrors.comment = t("cart.form.pleaseFillField", "Please fill out this field");
    }

    setFormErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (isLoading || !validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const attributes: Record<string, string | undefined> = {
        mpn: mpn,
        productLink: window.location.href,
      };

      attributes.comment = formData.comment.trim();

      const aggregateAttributes = {
        ...attributes,
        ...formAttributes,
      };

      const result = await services.api.client.mutate({
        mutation: LeadCreateDocument,
        variables: {
          input: {
            mail: formData.email.trim(),
            formName: formName,
            attributes: JSON.stringify(aggregateAttributes),
            name: "",
            source: "",
            phone: services.formatter.phoneCleaner(formData.phone),
          },
        },
      });

      if (result?.data?.LeadCreate) {
        setIsSuccess(true);
        setFormData({ email: "", phone: "", comment: "" });
        setFormErrors({ email: "", phone: "", comment: "" });
      } else {
        console.error("Server error:", result?.errors);
      }
    } catch (error) {
      console.error("Error in send process:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, validateForm, formData, mpn, services]);

  const renderHead = () => {
    return (
      <div className={styles.headerContainer}>
        <div className={styles.header}>{title}</div>
        <div className={styles.subHeader}>{subTitle}</div>
      </div>
    );
  };

  const renderBody = () => {
    return (
      <div className={styles.fieldsContainer}>
        <Input
          id="email-input"
          name="email"
          type="email"
          placeholder={t("9A9C1A1F52B34DD9.your-email", "Your email")}
          customError={formErrors.email}
          clearable
          required
          value={formData.email}
          onChange={changeHandler}
          icon={<Icon src={mailIcon} width={17} height={17} className={styles.prefixIcon} />}
          iconPosition="prefix"
        />
        <Input
          id="phone-input"
          name="phone"
          type="tel"
          placeholder={t("87c03r37rm8a2tms.phone-number", "Phone number")}
          customError={formErrors.phone}
          clearable
          required
          value={formData.phone}
          onChange={changeHandler}
          icon={<Icon src={phoneIcon} width={17} height={17} className={styles.prefixIcon} />}
          iconPosition="prefix"
        />
        <Textarea
          id="comment-input"
          name="comment"
          placeholder={t("sn98u34ea89fhane.add-comment-placeholder", "Add a comment to a request")}
          customError={formErrors.comment}
          clearable
          required
          value={formData.comment}
          onChange={changeHandler}
          rows={2}
          resize="vertical"
        />
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <div className={styles.footerContainer}>
        <FixButton
          className={styles.buttonSubmit}
          onClick={handleSubmit}
          variant="primary"
          size="large"
          loading={isLoading}
          disabled={isLoading}
          prefix={customize?.buttonIcon && <Icon src={customize?.buttonIcon} height={15} />}
        >
          {buttonText}
        </FixButton>
        <div className={styles.subButtonText}>
          <Icon src={lockIcon} />
          {t("a784gfbairsbsu9q.your-data-is-secure", "Your data is secure and never shared")}
        </div>
      </div>
    );
  };

  return isSuccess ? <LeadFormSuccess /> : <FixForm head={renderHead()} body={renderBody()} footer={renderFooter()} />;
};

export default LeadForm;
